import React from "react";
import { FileText, Layers, Globe } from "lucide-react";

import { Card } from "@/components/ui/Card";
import Switch from "@/components/ui/Switch";

import { ChecklistCardActionsMenu, CountryMasterReviewNotice } from "../index";

// Documents previewed in a card's weight box before the "+N more" row.
const DOC_PREVIEW_LIMIT = 3;

// Counts come from the list summary. They are shown only when the backend
// actually sent them — an absent count is reported as such rather than
// defaulting to 0, which would read as "this checklist is empty".
const countLabel = (value, singular, plural) =>
    typeof value === "number" ? `${value} ${value === 1 ? singular : plural}` : "No data found";

/**
 * One checklist row. Rendered from both the "Country document sets" and
 * "Your checklists" sections of the list page (Phase 6 grouping) so a later
 * change to the card — a new badge, a new counter — lands in both instead of
 * needing to be repeated.
 */
const ChecklistCard = ({
    cl,
    writesBlocked,
    canManageMasters,
    onView,
    onEdit,
    onHistory,
    onDelete,
    onUseTemplate,
    onToggleStatus,
    onDismissReview,
}) => {
    const allDocs = (cl.sections ?? []).flatMap((sec) =>
        (sec.subsections ?? []).map((sub) => ({
            id: sub.id,
            name: sub.name,
            weightPercentage: sub.weightPercentage,
        }))
    );
    const totalDocs = cl.subsectionCount;
    const totalSecs = cl.sectionCount;

    return (
        <Card
            role="button"
            tabIndex={0}
            onClick={onView}
            onKeyDown={(e) => e.key === "Enter" && onView()}
            className="group p-4 flex flex-col gap-2.5 cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tone-emerald/60"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="font-medium text-sm text-text-primary truncate">{cl.name}</div>
                    <div className="text-[11.5px] text-text-primary/60 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span>
                            {cl.region ? `${cl.region} · ` : ""}v{cl.version}
                        </span>
                        {/* A country document set is a source, not a
                            checklist anyone is assigned to. Marked on the
                            card because the two can still land in the same
                            section (e.g. a filtered search) and the
                            available actions differ. */}
                        {cl.isCountryMaster && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-tone-blue/40 bg-tone-blue-soft/40 px-1.5 py-0.5 text-[10px] font-medium text-tone-blue">
                                <Globe size={9} />
                                Country set
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span
                            className={`text-[11px] font-medium ${
                                cl.isActive ? "text-tone-emerald" : "text-text-secondary"
                            }`}
                        >
                            {cl.isActive ? "Active" : "Inactive"}
                        </span>
                        <Switch
                            size="sm"
                            checked={cl.isActive}
                            disabled={writesBlocked}
                            onToggle={onToggleStatus}
                            label={`${cl.name} active`}
                            title={cl.isActive ? "Click to set Inactive" : "Click to set Active"}
                        />
                    </div>
                    <ChecklistCardActionsMenu
                        writesBlocked={writesBlocked}
                        isCountryMaster={!!cl.isCountryMaster}
                        canManageMasters={canManageMasters}
                        onUseTemplate={onUseTemplate}
                        onView={onView}
                        onEdit={onEdit}
                        onHistory={onHistory}
                        onDelete={onDelete}
                    />
                </div>
            </div>

            {/* CM-7 - the set came back after a revocation, or the platform's
                version moved on. One merged message either way; the copy is
                never overwritten. A sibling of the header row, not nested in
                its name column, so it spans the full card instead of only
                the width left over beside the Active switch and menu. */}
            <CountryMasterReviewNotice
                wasRestored={!!cl.wasRestored}
                templateMovedOn={!!cl.templateMovedOn}
                needsReview={!!cl.needsReview}
                writesBlocked={writesBlocked}
                onDismiss={onDismissReview}
            />

            {/* Counters */}
            <div className="flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border-primary bg-background-form/40 text-[11px] text-text-primary/70">
                    <FileText size={10} />
                    {countLabel(totalDocs, "document", "documents")}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border-primary bg-background-form/40 text-[11px] text-text-primary/70">
                    <Layers size={10} />
                    {countLabel(totalSecs, "section", "sections")}
                </span>
            </div>

            {/* Weight distribution preview */}
            <div className="rounded-xl bg-background-form/30 border border-border-primary/60 px-2.5 py-2">
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10.5px] font-semibold tracking-wide text-text-primary">
                        Documents
                    </span>
                </div>

                {allDocs.length > 0 ? (
                    <div className="flex flex-col gap-1">
                        {allDocs.slice(0, DOC_PREVIEW_LIMIT).map((doc) => (
                            <div key={doc.id ?? doc.name} className="flex items-center justify-between gap-2">
                                <span className="truncate text-[11.5px] text-text-primary">
                                    · {doc.name}
                                    {doc.isRequired !== false && (
                                        <span
                                            className="text-red-500 font-bold text-xs ml-0.5"
                                            title="Required Document"
                                        >
                                            *
                                        </span>
                                    )}
                                </span>
                                <span className="shrink-0 text-[11.5px] font-semibold text-text-primary">
                                    {typeof doc.weightPercentage === "number" ? `${doc.weightPercentage}%` : "—"}
                                </span>
                            </div>
                        ))}
                        {allDocs.length > DOC_PREVIEW_LIMIT && (
                            <span className="text-[11px] text-text-primary">
                                +{allDocs.length - DOC_PREVIEW_LIMIT} more
                                {allDocs.length - DOC_PREVIEW_LIMIT === 1 ? " document" : " documents"}
                            </span>
                        )}
                    </div>
                ) : (
                    // The list response carries counts but no tree yet
                    // (BACKEND_REQUIREMENTS #1) — say how many there are
                    // and point at the detail view rather than claiming none.
                    <p className="text-[11.5px] text-text-primary">
                        {typeof totalDocs === "number" && totalDocs > 0
                            ? `${totalDocs} document${totalDocs === 1 ? "" : "s"} — view details`
                            : "No documents added"}
                    </p>
                )}
            </div>
        </Card>
    );
};

export default ChecklistCard;
